import type { AxiosRequestConfig } from 'axios';
import api from '../../../shared/utils/api';

/**
 * Voice (SDR talking-agent) — a TIERED add-on with an ISOLATED credit pool,
 * separate from the general "sales" credit pool shown in the header
 * CreditBalancePopover.
 *
 * FRENTE C — desbundled voice commercial model (owner-ratified):
 *   - Voice is a tiered add-on: Voz Starter / Voz Pro / Voz Enterprise.
 *   - VC (voice credits) is now an INTERNAL metric — the user sees MINUTES,
 *     never "VC / créditos de voz" as a primary number.
 *     `minutos_restantes = available_vc / vc_per_minute` (vc_per_minute = 2).
 *   - Prepaid, FAIL-CLOSED, NO overage: when minutes run out, dialing STOPS.
 *     The only CTAs are "Comprar minutos extra" (top-up) and
 *     "Fazer upgrade de tier". There is NO overage/postpaid UI in the pilot.
 *
 * BFF surface (mounted at /api/v1/billing/voice/* — never the backend
 * directly, per the FE→BFF architecture rule):
 *   GET  /billing/voice/balance               → VoiceBalance (404 when OFF)
 *   GET  /billing/voice/addon/status          → VoiceAddonStatus (tier snapshot)
 *   GET  /billing/voice/ledger                → VoiceLedgerEntry[]
 *   GET  /billing/voice/reservations          → VoiceReservation[]
 *   POST /billing/voice/topup {pack_id}
 *   GET  /billing/voice/addon/telephony-usage → VoiceTelephonyUsage (Frente C gap 1)
 *   GET  /billing/voice/addon/topup/options   → VoiceTopupOptions (Frente C gap 2)
 *   POST /billing/voice/addon/topup {pack_id} (Frente C gap 2)
 *   POST /billing/voice/addon/change-tier {new_tier_slug, idempotency_key} (gap 3)
 */

/** VC consumed per minute of voice (vc_per_minute = 2). */
export const VC_PER_MINUTE = 2;

/** Convert an (internal) VC amount to user-facing minutes. */
export function vcToMinutes(vc: number | null | undefined): number {
  if (vc == null || Number.isNaN(vc)) return 0;
  return Math.floor(vc / VC_PER_MINUTE);
}

export interface VoiceLot {
  /** Origin of the lot: plan grant, topup pack, bonus, etc. */
  source: string;
  /** Remaining VC in this lot. */
  amount_remaining: number;
  /** ISO timestamp when this lot expires (null = no expiry). */
  expires_at: string | null;
}

export interface VoiceBalance {
  /** Spendable VC right now (total_valido − reservado). */
  available_vc: number;
  /** Total non-expired VC granted. */
  total_valido: number;
  /** VC held by in-flight reservations (calls in progress). */
  reservado: number;
  /** Per-lot breakdown with expiry. */
  lots: VoiceLot[];
}

/**
 * Active voice tier add-on snapshot. `active: false` means the tenant has no
 * voice plan ("sem plano de voz / contratar"). All other fields are present
 * only when `active: true`.
 *
 * `overage_rate_brl` is intentionally NOT surfaced — the pilot is fail-closed
 * with NO overage. It is omitted from this type so it can never leak into the UI.
 */
export interface VoiceAddonStatus {
  active: boolean;
  /** Tier slug: 'voz.starter' | 'voz.pro' | 'voz.enterprise'. */
  tier_slug?: string;
  status?: string;
  /** Monthly tier price (BRL). */
  price_brl?: number;
  /** Included VC allowance per cycle (INTERNAL — convert to minutes for UI). */
  allowance_vc?: number;
  /** Max simultaneous calls allowed by the tier. */
  max_concurrent?: number;
  /** ISO timestamp when the add-on started. */
  started_at?: string | null;
  addon_id?: number;
  [k: string]: unknown;
}

/**
 * Static commercial metadata per tier slug (display name + included minutes).
 * The status endpoint returns `tier_slug` + `allowance_vc` but not a human
 * label, so we map it here. `included_minutes` mirrors `allowance_vc / 2` and
 * is kept for display fallback if `allowance_vc` is ever absent.
 *
 * IMPORTANT: declaration order = tier RANK (cheapest → priciest). The voice
 * panel uses `Object.keys(VOICE_TIERS).indexOf(slug)` to classify a tier
 * change as upgrade (self-serve, gateway-wired) or downgrade (support
 * ticket). Adding a tier in the wrong slot silently rewires that logic —
 * `voz.basic` MUST stay above `voz.starter` so basic→starter is an upgrade
 * and starter→basic is a downgrade.
 */
export const VOICE_TIERS: Record<
  string,
  { name: string; includedMinutes: number; maxConcurrent: number; priceBrl: number }
> = {
  'voz.basic': { name: 'Voz Basic', includedMinutes: 120, maxConcurrent: 1, priceBrl: 180 },
  'voz.starter': { name: 'Voz Starter', includedMinutes: 800, maxConcurrent: 4, priceBrl: 1200 },
  'voz.pro': { name: 'Voz Pro', includedMinutes: 1400, maxConcurrent: 6, priceBrl: 2000 },
  'voz.enterprise': {
    name: 'Voz Enterprise',
    includedMinutes: 4000,
    maxConcurrent: 8,
    priceBrl: 4500,
  },
};

/** Human label for a tier slug, falling back to the raw slug. */
export function tierName(slug: string | undefined): string {
  if (!slug) return '—';
  return VOICE_TIERS[slug]?.name ?? slug;
}

export interface VoiceLedgerEntry {
  id?: string | number;
  created_at?: string;
  /** debit / credit / reserve / release / topup ... */
  type?: string;
  /** Signed VC delta (negative = consumed). */
  amount?: number;
  description?: string;
  [k: string]: unknown;
}

export interface VoiceReservation {
  id?: string | number;
  amount?: number;
  created_at?: string;
  expires_at?: string | null;
  status?: string;
  [k: string]: unknown;
}

export interface VoiceTopupResponse {
  /** Hosted checkout URL when the topup requires payment. */
  checkout_url?: string;
  pack_id?: string;
  status?: string;
  [k: string]: unknown;
}

/**
 * Telephony consumed THIS CALENDAR MONTH (Frente C gap 1 — telephony-consumed
 * card). The raw billing payload: numbers MAY arrive as strings — coerce with
 * `toNum()` before display. Distinct from voice-credit (VC) consumption: this
 * is the metered telephony spend (PSTN minutes routed through the carrier).
 */
export interface VoiceTelephonyUsageRaw {
  tenant_id?: number | string;
  account_id?: number | string;
  period_start?: string;
  period_end?: string;
  total_minutes?: number | string;
  total_brl?: number | string;
  unit_price_brl?: number | string;
  call_count?: number | string;
  [k: string]: unknown;
}

/** Coerced, display-ready telephony usage for the current month. */
export interface VoiceTelephonyUsage {
  periodStart: string | null;
  periodEnd: string | null;
  totalMinutes: number;
  totalBrl: number;
  unitPriceBrl: number;
  callCount: number;
}

/** A single voice top-up pack from the catalog. `priceBrl` is null when billing
 * reports no price source — the UI shows "sob consulta" and disables purchase. */
export interface VoiceTopupPack {
  packId: string;
  vc: number;
  minutes: number;
  priceBrl: number | null;
  validityDays: number | null;
}

interface VoiceTopupPackRaw {
  pack_id?: string;
  vc?: number | string;
  minutes?: number | string;
  price_brl?: number | string | null;
  validity_days?: number | string | null;
  [k: string]: unknown;
}

interface VoiceTopupOptionsRaw {
  packs?: VoiceTopupPackRaw[];
  [k: string]: unknown;
}

/**
 * Result of a gateway-wired tier change (Frente C gap 3). Billing returns a
 * `sucesso` envelope on BOTH success and failure. On a non-2xx the axios error
 * carries this body too — the caller throws a `VoiceChangeTierError` so the UI
 * can branch on `code`.
 */
export interface VoiceChangeTierResult {
  sucesso: boolean;
  tier_slug?: string;
  allowance_vc?: number;
  max_concurrent?: number;
  price_brl?: number;
  changed_from?: string;
  code?: string;
  erro?: string;
  [k: string]: unknown;
}

/** Thrown when change-tier fails — surfaces billing's `code` + http status so
 * the panel can map 502 gateway failures to a "fale com o suporte" message. */
export class VoiceChangeTierError extends Error {
  code?: string;
  httpStatus?: number;
  constructor(message: string, code?: string, httpStatus?: number) {
    super(message);
    this.name = 'VoiceChangeTierError';
    this.code = code;
    this.httpStatus = httpStatus;
  }
}

/** Coerce a value that may be a number, a numeric string, or null/undefined. */
function toNum(v: number | string | null | undefined): number {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Coerce a price that is allowed to be genuinely absent (null = no source). */
function toNullableNum(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Local view of the per-request flag honoured by the shared axios interceptor
 * (the flag interface there is internal — redeclare just what we need to stay
 * type-safe without an `as any`, mirroring documentIdentityApi.ts).
 */
type SkipErrorNotificationConfig = AxiosRequestConfig & {
  __skipErrorNotification?: boolean;
};

export const voiceCreditApi = {
  /**
   * Voice pool snapshot. Resolves to `null` when the pool is OFF (flag off /
   * tenant has no voice pool) — the BFF returns 404 in that case, which is BY
   * DESIGN (not an error). We pass `__skipErrorNotification` so the axios
   * interceptor does NOT fire the global red toast for the expected 404.
   * Callers treat `null` as "voice not available" and hide the panel.
   */
  getVoiceBalance: async (): Promise<VoiceBalance | null> => {
    try {
      const { data } = await api.get<VoiceBalance>('/billing/voice/balance', {
        __skipErrorNotification: true,
      } as SkipErrorNotificationConfig);
      return data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  /**
   * Active voice tier add-on status. Like the balance, the BFF returns 404 when
   * the voice feature flag is OFF — mapped to `null` (panel hidden). A tenant
   * with the feature on but no tier returns `{ active: false }` (200).
   */
  getVoiceAddonStatus: async (): Promise<VoiceAddonStatus | null> => {
    try {
      const { data } = await api.get<VoiceAddonStatus>('/billing/voice/addon/status', {
        __skipErrorNotification: true,
      } as SkipErrorNotificationConfig);
      return data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  getVoiceLedger: async (): Promise<VoiceLedgerEntry[]> => {
    const { data } = await api.get<VoiceLedgerEntry[] | { entries?: VoiceLedgerEntry[] }>(
      '/billing/voice/ledger',
    );
    if (Array.isArray(data)) return data;
    return data?.entries ?? [];
  },

  getVoiceReservations: async (): Promise<VoiceReservation[]> => {
    const { data } = await api.get<VoiceReservation[] | { reservations?: VoiceReservation[] }>(
      '/billing/voice/reservations',
    );
    if (Array.isArray(data)) return data;
    return data?.reservations ?? [];
  },

  topupVoice: async (packId: string): Promise<VoiceTopupResponse> => {
    const { data } = await api.post<VoiceTopupResponse>('/billing/voice/topup', {
      pack_id: packId,
    });
    return data;
  },

  /**
   * Telephony consumed this calendar month (Frente C gap 1). Resolves to `null`
   * when the voice feature is OFF (BFF 404 — by design, no toast). Numbers are
   * coerced from the raw billing payload (which may report them as strings).
   */
  getTelephonyUsage: async (): Promise<VoiceTelephonyUsage | null> => {
    try {
      const { data } = await api.get<VoiceTelephonyUsageRaw>(
        '/billing/voice/addon/telephony-usage',
        { __skipErrorNotification: true } as SkipErrorNotificationConfig,
      );
      return {
        periodStart: data?.period_start ?? null,
        periodEnd: data?.period_end ?? null,
        totalMinutes: toNum(data?.total_minutes),
        totalBrl: toNum(data?.total_brl),
        unitPriceBrl: toNum(data?.unit_price_brl),
        callCount: toNum(data?.call_count),
      };
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  /**
   * Voice top-up pack catalog (Frente C gap 2). Returns the coerced packs;
   * `priceBrl` stays `null` when billing reports no price source so the UI can
   * show "sob consulta" and disable the buy button for that pack. 404 (feature
   * off) → empty list.
   */
  getTopupOptions: async (): Promise<VoiceTopupPack[]> => {
    try {
      const { data } = await api.get<VoiceTopupOptionsRaw>('/billing/voice/addon/topup/options', {
        __skipErrorNotification: true,
      } as SkipErrorNotificationConfig);
      const packs = Array.isArray(data?.packs) ? data!.packs : [];
      return packs.map((p) => ({
        packId: String(p?.pack_id ?? ''),
        vc: toNum(p?.vc),
        minutes: toNum(p?.minutes),
        priceBrl: toNullableNum(p?.price_brl),
        validityDays: toNullableNum(p?.validity_days),
      }));
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) return [];
      throw err;
    }
  },

  /**
   * Buy a top-up pack (Frente C gap 2). POSTs the pack_id to the addon top-up
   * route (tenant_id injected by the BFF from the JWT). 201 on success.
   */
  buyTopupPack: async (packId: string): Promise<VoiceTopupResponse> => {
    const { data } = await api.post<VoiceTopupResponse>('/billing/voice/addon/topup', {
      pack_id: packId,
    });
    return data;
  },

  /**
   * Self-serve, GATEWAY-WIRED voice tier change (Frente C gap 3) — a REAL
   * upgrade that changes the monthly charge. POSTs {new_tier_slug,
   * idempotency_key}; tenant_id is injected by the BFF. On a billing failure
   * (e.g. 502 VOICE_GATEWAY_SUBSCRIBE_FAILED / 422 VOICE_TIER_INVALID, or a
   * 2xx body with `sucesso:false`) this throws a `VoiceChangeTierError` so the
   * caller can branch on `code`/`httpStatus`.
   */
  changeTier: async (
    newTierSlug: string,
    idempotencyKey: string,
  ): Promise<VoiceChangeTierResult> => {
    try {
      const { data } = await api.post<VoiceChangeTierResult>(
        '/billing/voice/addon/change-tier',
        { new_tier_slug: newTierSlug, idempotency_key: idempotencyKey },
        { __skipErrorNotification: true } as SkipErrorNotificationConfig,
      );
      if (data?.sucesso === false) {
        throw new VoiceChangeTierError(data.erro || 'Falha ao alterar o tier de voz', data.code);
      }
      return data;
    } catch (err: unknown) {
      if (err instanceof VoiceChangeTierError) throw err;
      const resp = (err as { response?: { status?: number; data?: VoiceChangeTierResult } })
        ?.response;
      const body = resp?.data;
      throw new VoiceChangeTierError(
        body?.erro || 'Falha ao alterar o tier de voz',
        body?.code,
        resp?.status,
      );
    }
  },
};
